import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight } from 'lucide-react';
import { eventsApi, type RaceEvent } from '@/api/events';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import { navT } from '@/i18n/messages/nav';
import { eventsWithResults } from '@/lib/resultsIndex';

/// #936 — destino do item "Resultados" do menu. A página de resultados é POR
/// evento (`/events/:id/results`); sem evento escolhido, o menu cai aqui pra
/// escolher. Mesma query (`['events']`) da lista de eventos: abrir uma depois
/// da outra não refaz a request.
export function ResultsIndexPage() {
  const t = navT().resultsIndex;
  const list = useQuery({ queryKey: ['events'], queryFn: eventsApi.list });
  const events = eventsWithResults(list.data ?? []);

  return (
    <div>
      <header className="border-b">
        <div className="container mx-auto py-3 sm:py-4">
          <h1 className="text-lg sm:text-xl font-semibold">{t.title}</h1>
        </div>
      </header>

      <main className="container mx-auto py-6 sm:py-10 space-y-6 max-w-6xl">
        <p className="text-muted-foreground text-sm">{t.intro}</p>

        {list.isLoading && <Spinner />}
        {list.isError && (
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-destructive">{t.loadFailed((list.error as Error).message)}</p>
            </CardContent>
          </Card>
        )}
        {list.data && events.length === 0 && (
          <Card>
            <CardHeader>
              <CardTitle>{t.emptyTitle}</CardTitle>
              <CardDescription>{t.emptyBody}</CardDescription>
            </CardHeader>
          </Card>
        )}

        <ul className="space-y-2">
          {events.map((ev) => (
            <li key={ev.id}>
              <Link
                to={`/events/${ev.id}/results`}
                title={t.open}
                className="flex items-center gap-3 rounded-xl border bg-card px-4 py-3 transition-colors hover:bg-muted"
              >
                <EventThumb ev={ev} />
                <div className="min-w-0 flex-1">
                  <p className="break-words font-medium">{ev.name}</p>
                  <p className="text-xs text-muted-foreground">{statusLabel(ev, t)}</p>
                </div>
                <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
              </Link>
            </li>
          ))}
        </ul>
      </main>
    </div>
  );
}

function statusLabel(ev: RaceEvent, t: ReturnType<typeof navT>['resultsIndex']): string {
  if (ev.status === 'finished') return t.finished;
  return ev.hasRunningDay ? t.running : t.published;
}

function EventThumb({ ev }: { ev: RaceEvent }) {
  return ev.logoUrl ? (
    <img src={ev.logoUrl} alt="" className="h-10 w-10 rounded-md border object-cover" />
  ) : (
    <div className="h-10 w-10 rounded-md border" style={{ backgroundColor: ev.primaryColor ?? '#0EA5E9' }} aria-hidden />
  );
}
